import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

/**
 * Professional voice input for the Coach (and reusable elsewhere).
 *
 * Behaviour upgrades vs. the previous version:
 *  - INTERIM results stream live to `onInterim(text)` while the user speaks
 *    so they can see what's being recognised — no more black-box pulsing.
 *  - APPENDS the final transcript to the existing input via `onResult(text)`
 *    instead of overwriting (caller controls the merge).
 *  - SILENCE-tolerant: auto-restarts during brief pauses, ends after
 *    `SILENCE_TIMEOUT_MS` of total silence after speech started.
 *  - Graceful, localised TOAST messages for every error code
 *    (`not-allowed`, `no-speech`, `network`, `audio-capture`, etc.).
 *  - Proactive mic-permission probe (Permissions API where available).
 *  - Idle/Listening icons are visually DIFFERENT (Mic vs. MicOff).
 *  - iOS Safari & PWA hint if the Web Speech API is missing.
 */

const SILENCE_TIMEOUT_MS = 8000;          // hard stop after this much continuous silence
const INTER_PAUSE_RESTART_MS = 1500;      // restart recognition if it ends prematurely within this window

export const VoiceInput = ({ onResult, onInterim, className = '', testid = 'voice-input-btn' }) => {
    const { language, t } = useLanguage();
    const [isListening, setIsListening] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [isSupported] = useState(() => {
        return typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    });
    const recognitionRef = useRef(null);
    const silenceTimerRef = useRef(null);
    const restartFlagRef = useRef(false);
    const lastSpeechAtRef = useRef(0);

    const errorMessages = {
        'not-allowed': language === 'de'
            ? 'Mikrofon-Zugriff verweigert. Bitte in den Browser-Einstellungen erlauben.'
            : 'Microphone access denied. Please allow it in browser settings.',
        'service-not-allowed': language === 'de'
            ? 'Spracherkennung blockiert. Bitte HTTPS und Mikro-Berechtigung prüfen.'
            : 'Speech recognition blocked. Check HTTPS + mic permission.',
        'no-speech': language === 'de'
            ? 'Habe nichts gehört, Buddy. Versuch es nochmal.'
            : "Didn't hear anything, Buddy. Try again.",
        'audio-capture': language === 'de'
            ? 'Kein Mikrofon gefunden.'
            : 'No microphone found.',
        'network': language === 'de'
            ? 'Spracherkennung benötigt eine Internet-Verbindung.'
            : 'Speech recognition needs an internet connection.',
        'aborted': null, // user-triggered abort — no toast
    };

    const cleanupRecognition = useCallback(() => {
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        const rec = recognitionRef.current;
        if (rec) {
            try { rec.onresult = null; rec.onerror = null; rec.onend = null; rec.abort(); } catch { /* noop */ }
        }
        recognitionRef.current = null;
        restartFlagRef.current = false;
    }, []);

    useEffect(() => () => cleanupRecognition(), [cleanupRecognition]);

    const startSilenceTimer = useCallback(() => {
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = setTimeout(() => {
            restartFlagRef.current = false;
            const rec = recognitionRef.current;
            if (rec) {
                try { rec.stop(); } catch { /* noop */ }
            }
        }, SILENCE_TIMEOUT_MS);
    }, []);

    const buildRecognition = useCallback(() => {
        const Klass = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!Klass) return null;
        const rec = new Klass();
        rec.continuous = true;       // collect multiple utterances
        rec.interimResults = true;   // stream partial text
        rec.lang = language === 'de' ? 'de-DE' : 'en-US';
        rec.maxAlternatives = 1;
        return rec;
    }, [language]);

    const handleStop = useCallback(() => {
        restartFlagRef.current = false;
        const rec = recognitionRef.current;
        if (rec) {
            try { rec.stop(); } catch { /* noop */ }
        }
    }, []);

    const handleStart = useCallback(async () => {
        if (!isSupported) {
            toast.error(language === 'de'
                ? 'Spracheingabe wird auf diesem Gerät nicht unterstützt. Tipp: In Safari/Chrome auf dem Mac/Android nutzen.'
                : 'Voice input not supported on this device. Try Safari/Chrome on Mac/Android.');
            return;
        }
        // Pre-check microphone permission via Permissions API where available
        try {
            if (navigator.permissions && navigator.permissions.query) {
                const status = await navigator.permissions.query({ name: 'microphone' });
                if (status.state === 'denied') {
                    toast.error(errorMessages['not-allowed']);
                    return;
                }
            }
        } catch { /* not all browsers support the query */ }

        setIsStarting(true);
        cleanupRecognition();
        const rec = buildRecognition();
        if (!rec) {
            setIsStarting(false);
            return;
        }
        recognitionRef.current = rec;
        restartFlagRef.current = true;
        lastSpeechAtRef.current = Date.now();
        let finalChunk = '';

        rec.onstart = () => {
            setIsStarting(false);
            setIsListening(true);
            startSilenceTimer();
        };

        rec.onresult = (event) => {
            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const r = event.results[i];
                if (r.isFinal) {
                    finalChunk += r[0].transcript;
                } else {
                    interim += r[0].transcript;
                }
            }
            lastSpeechAtRef.current = Date.now();
            startSilenceTimer();
            if (onInterim) {
                onInterim((finalChunk + ' ' + interim).trim());
            }
        };

        rec.onerror = (event) => {
            const msg = errorMessages[event.error];
            if (msg) toast.error(msg);
            restartFlagRef.current = false; // don't auto-restart on errors
        };

        rec.onend = () => {
            // Auto-restart if we still want to listen (silence-tolerant continuous mode)
            const elapsedSinceSpeech = Date.now() - lastSpeechAtRef.current;
            if (restartFlagRef.current && elapsedSinceSpeech < INTER_PAUSE_RESTART_MS) {
                try { rec.start(); return; } catch { /* fall through */ }
            }
            // Final commit
            const trimmed = finalChunk.trim();
            if (trimmed && onResult) onResult(trimmed);
            setIsListening(false);
            setIsStarting(false);
            cleanupRecognition();
        };

        try {
            rec.start();
        } catch (e) {
            setIsStarting(false);
            setIsListening(false);
            toast.error(errorMessages['not-allowed']);
            cleanupRecognition();
        }
    }, [isSupported, language, errorMessages, buildRecognition, cleanupRecognition, onInterim, onResult, startSilenceTimer]);

    const toggleListening = useCallback(() => {
        if (isListening || isStarting) {
            handleStop();
        } else {
            handleStart();
        }
    }, [isListening, isStarting, handleStart, handleStop]);

    if (!isSupported) {
        // Render a disabled tooltip-style button so layout stays consistent and users get feedback
        return (
            <Button
                type="button"
                variant="outline"
                size="icon"
                disabled
                title={language === 'de'
                    ? 'Spracheingabe nicht verfügbar (kein Web Speech API in iOS PWA)'
                    : 'Voice input unavailable (no Web Speech API in iOS PWA)'}
                data-testid={`${testid}-disabled`}
                className={`opacity-50 ${className}`}
            >
                <MicOff className="w-4 h-4" />
            </Button>
        );
    }

    return (
        <Button
            type="button"
            variant={isListening ? 'default' : 'outline'}
            size="icon"
            onClick={toggleListening}
            disabled={isStarting}
            className={`relative ${isListening ? 'bg-red-500 hover:bg-red-600 text-white' : ''} ${className}`}
            title={isListening ? t('voice_listening') : t('voice_click_to_speak')}
            aria-pressed={isListening}
            data-testid={testid}
        >
            {isStarting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
            ) : isListening ? (
                <Mic className="w-4 h-4" />
            ) : (
                <MicOff className="w-4 h-4" />
            )}
            {isListening && (
                <>
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
                </>
            )}
        </Button>
    );
};

/**
 * Lightweight hook variant — for callers that want full control of UI.
 * Same behaviour upgrades as the component above.
 */
export const useVoiceInput = () => {
    const { language } = useLanguage();
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(true);
    const [transcript, setTranscript] = useState('');

    const startListening = useCallback((onResult) => {
        const Klass = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!Klass) {
            setIsSupported(false);
            return;
        }
        const recognition = new Klass();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = language === 'de' ? 'de-DE' : 'en-US';

        recognition.onresult = (event) => {
            const result = event.results[0][0].transcript;
            setTranscript(result);
            if (onResult) onResult(result);
            setIsListening(false);
        };
        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);

        try {
            recognition.start();
            setIsListening(true);
        } catch {
            setIsListening(false);
        }
    }, [language]);

    return { isListening, isSupported, transcript, startListening };
};
